import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, X, Clock, TrendingUp, Zap, Calendar, 
  ChevronDown, AlertTriangle, CheckCircle, Info, Scale
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent } from './ui/card';

// Market hours data
const MARKETS = {
  US: {
    name: 'NYSE / NASDAQ',
    flag: '🇺🇸',
    openHour: 9,
    openMinute: 30,
    closeHour: 16,
    closeMinute: 0,
    timezone: 'America/New_York',
    utcOffset: -5, // EST (changes with DST)
  },
  FR: {
    name: 'Euronext Paris',
    flag: '🇫🇷',
    openHour: 9,
    openMinute: 0,
    closeHour: 17,
    closeMinute: 30,
    timezone: 'Europe/Paris',
    utcOffset: 1, // CET (changes with DST)
  }
};

// Timezone options
const TIMEZONES = [
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: '+1/+2' },
  { value: 'America/New_York', label: 'New York (ET)', offset: '-5/-4' },
  { value: 'Europe/London', label: 'Londres (GMT/BST)', offset: '0/+1' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: '+9' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', offset: '+10/+11' },
];

// Convert time from market timezone to display timezone
const convertTime = (hour, minute, fromTz, toTz) => {
  // Create a date object for today with the specified time in the source timezone
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  
  // Create a date string that represents the time in the source timezone
  // We use a trick: create a date, format it in source TZ, then reparse
  const sourceTimeStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
  
  // Get the UTC time for this local time in the source timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: fromTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Create date assuming local time, then adjust
  const tempDate = new Date(sourceTimeStr);
  
  // Get what time it would be in the target timezone when it's hour:minute in source timezone
  // We need to find the UTC offset difference
  const sourceFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: fromTz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  const targetFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: toTz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // Simple approach: use fixed offsets for common conversions
  // US Eastern to Paris: +6 hours (EST) or +6 hours (EDT to CEST)
  // Paris to US Eastern: -6 hours
  
  let resultHour = hour;
  let resultMinute = minute;
  
  if (fromTz === 'America/New_York' && toTz === 'Europe/Paris') {
    // New York to Paris: add 6 hours (approximate, handles DST roughly)
    resultHour = hour + 6;
    if (resultHour >= 24) resultHour -= 24;
  } else if (fromTz === 'Europe/Paris' && toTz === 'America/New_York') {
    // Paris to New York: subtract 6 hours
    resultHour = hour - 6;
    if (resultHour < 0) resultHour += 24;
  } else if (fromTz === 'America/New_York' && toTz === 'Europe/London') {
    // New York to London: add 5 hours
    resultHour = hour + 5;
    if (resultHour >= 24) resultHour -= 24;
  } else if (fromTz === 'Europe/Paris' && toTz === 'Europe/London') {
    // Paris to London: subtract 1 hour
    resultHour = hour - 1;
    if (resultHour < 0) resultHour += 24;
  } else if (fromTz === 'America/New_York' && toTz === 'Asia/Tokyo') {
    // New York to Tokyo: add 14 hours (EST) or 13 hours (EDT)
    resultHour = hour + 13;
    if (resultHour >= 24) resultHour -= 24;
  } else if (fromTz === 'Europe/Paris' && toTz === 'Asia/Tokyo') {
    // Paris to Tokyo: add 7-8 hours
    resultHour = hour + 7;
    if (resultHour >= 24) resultHour -= 24;
  } else if (fromTz === 'America/New_York' && toTz === 'Australia/Sydney') {
    // New York to Sydney: add 15-16 hours
    resultHour = hour + 15;
    if (resultHour >= 24) resultHour -= 24;
  } else if (fromTz === 'Europe/Paris' && toTz === 'Australia/Sydney') {
    // Paris to Sydney: add 9-10 hours
    resultHour = hour + 9;
    if (resultHour >= 24) resultHour -= 24;
  }
  // If same timezone or not handled, keep original
  
  return {
    hour: resultHour,
    minute: resultMinute,
    formatted: `${String(resultHour).padStart(2, '0')}:${String(resultMinute).padStart(2, '0')}`
  };
};

// Get market status
export const getMarketStatus = (market) => {
  const now = new Date();
  const marketTz = MARKETS[market].timezone;
  
  // Get current time in market timezone
  const marketTime = new Date(now.toLocaleString('en-US', { timeZone: marketTz }));
  const currentHour = marketTime.getHours();
  const currentMinute = marketTime.getMinutes();
  const currentDay = marketTime.getDay(); // 0 = Sunday, 6 = Saturday
  
  const { openHour, openMinute, closeHour, closeMinute } = MARKETS[market];
  
  // Check if weekend
  if (currentDay === 0 || currentDay === 6) {
    return { isOpen: false, status: 'weekend', message: 'Fermé (week-end)' };
  }
  
  const currentMinutes = currentHour * 60 + currentMinute;
  const openMinutes = openHour * 60 + openMinute;
  const closeMinutes = closeHour * 60 + closeMinute;
  
  if (currentMinutes < openMinutes) {
    const diff = openMinutes - currentMinutes;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return { 
      isOpen: false, 
      status: 'pre-market',
      message: `Ouvre dans ${hours}h ${mins}min`,
      minutesUntilChange: diff
    };
  } else if (currentMinutes >= closeMinutes) {
    return { isOpen: false, status: 'closed', message: 'Fermé' };
  } else {
    const diff = closeMinutes - currentMinutes;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return { 
      isOpen: true, 
      status: 'open',
      message: `Ferme dans ${hours}h ${mins}min`,
      minutesUntilChange: diff
    };
  }
};

// Market Hours Display Component
export const MarketHoursDisplay = ({ compact = false, selectedTimezone = 'Europe/Paris' }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timezone, setTimezone] = useState(selectedTimezone);
  
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);
  
  const usStatus = getMarketStatus('US');
  
  // Get the short timezone label
  const getTimezoneLabel = (tz) => {
    const labels = {
      'Europe/Paris': 'Paris',
      'America/New_York': 'New York',
      'Europe/London': 'Londres',
      'Asia/Tokyo': 'Tokyo',
      'Australia/Sydney': 'Sydney'
    };
    return labels[tz] || tz;
  };
  
  // US Market hours (always local US time)
  const usOpenFormatted = "09:30";
  const usCloseFormatted = "16:00";
  
  // Convert US hours to selected timezone
  const getConvertedHours = () => {
    const usOpen = convertTime(9, 30, 'America/New_York', timezone);
    const usClose = convertTime(16, 0, 'America/New_York', timezone);
    return {
      open: usOpen.formatted,
      close: usClose.formatted,
      full: `${usOpen.formatted} - ${usClose.formatted}`
    };
  };
  
  const convertedHours = getConvertedHours();
  const tzLabel = getTimezoneLabel(timezone);
  
  if (compact) {
    return (
      <div className="flex items-center gap-3 text-sm">
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${usStatus.isOpen ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          <span className={`w-2 h-2 rounded-full ${usStatus.isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
          <span>🇺🇸 US {usStatus.isOpen ? 'OUVERT' : 'FERMÉ'}</span>
        </div>
      </div>
    );
  }
  
  return (
    <Card className="bg-card/50 border-white/10">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Horaires Marché US (NYSE/NASDAQ)
          </h3>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger className="w-[180px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {/* US Market - Local Time (never changes) */}
          <div className={`p-3 rounded-lg border ${usStatus.isOpen ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">🇺🇸 Heure US (locale)</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${usStatus.isOpen ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {usStatus.isOpen ? 'OUVERT' : 'FERMÉ'}
              </span>
            </div>
            <div className="text-sm font-medium">
              {usOpenFormatted} - {usCloseFormatted}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Heure New York (ET)
            </div>
            <div className={`text-xs mt-1 ${usStatus.isOpen ? 'text-green-400' : 'text-orange-400'}`}>
              {usStatus.message}
            </div>
          </div>
          
          {/* Conversion to selected timezone */}
          <div className={`p-3 rounded-lg border ${usStatus.isOpen ? 'border-blue-500/30 bg-blue-500/5' : 'border-gray-500/30 bg-gray-500/5'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">🔄 Conversion</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                {tzLabel}
              </span>
            </div>
            <div className="text-sm font-medium">
              {convertedHours.full}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Heure {tzLabel}
            </div>
            <div className={`text-xs mt-1 ${usStatus.isOpen ? 'text-green-400' : 'text-orange-400'}`}>
              {usStatus.message}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// First-time popup for Intraday
export const IntradayInfoPopup = ({ open, onClose, onDontShowAgain }) => {
  const [dontShow, setDontShow] = useState(false);
  
  const handleClose = () => {
    if (dontShow) {
      onDontShowAgain();
    }
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-yellow-500/10 to-card border-yellow-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Zap className="w-6 h-6 text-yellow-400" />
            Caractéristiques des Trades INTRADAY
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 text-sm">
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4" /> Durée Recommandée
            </h4>
            <p className="text-muted-foreground">
              Les trades Intraday sont conçus pour être clôturés dans la même journée de trading, 
              idéalement <strong className="text-yellow-400">sous 24 heures</strong>.
            </p>
            <p className="text-orange-400 text-xs mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Au-delà de 24h, un avertissement apparaîtra sur votre trade.
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-background/50 border border-white/10">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" /> Take Profits Adaptés
            </h4>
            <p className="text-muted-foreground">
              Les niveaux TP sont calculés pour des mouvements atteignables en une session.
              Les concepts ICT restent la base de l'analyse, adaptés au timeframe court.
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-background/50 border border-white/10">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4" /> Horaires des Marchés
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">🇺🇸 US :</span>
                <span className="ml-1">15:30 - 22:00 (Paris)</span>
              </div>
              <div>
                <span className="text-muted-foreground">🇫🇷 FR :</span>
                <span className="ml-1">09:00 - 17:30 (Paris)</span>
              </div>
            </div>
          </div>
          
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <h4 className="font-semibold flex items-center gap-2 mb-1 text-blue-400">
              💡 Conseil
            </h4>
            <p className="text-muted-foreground text-xs">
              Évitez de lancer des trades Intraday juste avant la fermeture du marché. 
              Privilégiez les premières heures après l'ouverture.
            </p>
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2 mr-auto">
            <Checkbox 
              id="dontShowIntraday" 
              checked={dontShow}
              onCheckedChange={setDontShow}
            />
            <label htmlFor="dontShowIntraday" className="text-sm text-muted-foreground cursor-pointer">
              Ne plus afficher ce message
            </label>
          </div>
          <Button onClick={handleClose} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            Compris
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// First-time popup for Swing
export const SwingInfoPopup = ({ open, onClose, onDontShowAgain }) => {
  const [dontShow, setDontShow] = useState(false);
  
  const handleClose = () => {
    if (dontShow) {
      onDontShowAgain();
    }
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-primary/10 to-card border-primary/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="w-6 h-6 text-primary" />
            Caractéristiques des Trades SWING
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 text-sm">
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4" /> Durée Typique
            </h4>
            <p className="text-muted-foreground">
              Les trades Swing sont conçus pour capturer des mouvements sur 
              <strong className="text-primary"> plusieurs jours à plusieurs semaines</strong>.
            </p>
            <p className="text-xs mt-2 text-muted-foreground">
              Durée moyenne : 2 à 15 jours selon les conditions de marché.
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-background/50 border border-white/10">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" /> Take Profits Étendus
            </h4>
            <p className="text-muted-foreground">
              Les niveaux TP visent des mouvements plus importants :
            </p>
            <ul className="text-xs mt-2 space-y-1 text-muted-foreground">
              <li>• <strong>TP1 (25%)</strong> : Premier niveau de sécurisation</li>
              <li>• <strong>TP2 (25%)</strong> : Deuxième niveau de prise de profit</li>
              <li>• <strong>TP100 (50%)</strong> : Objectif final basé sur la structure</li>
            </ul>
          </div>
          
          <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <h4 className="font-semibold flex items-center gap-2 mb-1">
              ⏳ Patience Requise
            </h4>
            <p className="text-muted-foreground text-xs">
              Les trades Swing nécessitent de la patience. Le prix peut consolider 
              ou retracer avant d'atteindre vos objectifs.
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <h4 className="font-semibold flex items-center gap-2 mb-1 text-blue-400">
              💡 Conseils
            </h4>
            <ul className="text-muted-foreground text-xs space-y-1">
              <li>• Vérifiez vos trades 1-2 fois par jour maximum</li>
              <li>• Laissez respirer le trade sans intervenir prématurément</li>
              <li>• Les TP partiels sécurisent vos gains progressivement</li>
            </ul>
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2 mr-auto">
            <Checkbox 
              id="dontShowSwing" 
              checked={dontShow}
              onCheckedChange={setDontShow}
            />
            <label htmlFor="dontShowSwing" className="text-sm text-muted-foreground cursor-pointer">
              Ne plus afficher ce message
            </label>
          </div>
          <Button onClick={handleClose} className="bg-primary hover:bg-primary/90">
            Compris
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Info Guide Panel (Sheet)
const InfoGuidePanel = ({ children }) => {
  const [selectedTimezone, setSelectedTimezone] = useState('Europe/Paris');
  
  return (
    <Sheet>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent 
        className="w-[400px] sm:w-[500px] overflow-y-auto pt-14"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Guide TRADEX AI
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Legal Disclaimer - Avertissement Juridique */}
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                Avertissement Important
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Aucun conseil en investissement :</strong> TRADEX AI est un outil d'aide à la décision uniquement informatif. Les analyses ne constituent en aucun cas des conseils en investissement.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Scale className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Risques du trading :</strong> Le trading comporte des risques élevés de perte en capital. N'investissez jamais plus que ce que vous pouvez vous permettre de perdre.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Performances passées :</strong> Les performances passées ne préjugent pas des performances futures.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Market Hours */}
          <MarketHoursDisplay selectedTimezone={selectedTimezone} />
          
          {/* Trade Types Comparison */}
          <Card className="bg-card/50 border-white/10">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Types de Trades
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2"></th>
                      <th className="text-center py-2 text-yellow-400">⚡ INTRADAY</th>
                      <th className="text-center py-2 text-primary">📈 SWING</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-white/5">
                      <td className="py-2">Durée</td>
                      <td className="text-center py-2">&lt; 24 heures</td>
                      <td className="text-center py-2">2-15 jours</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">Timeframe</td>
                      <td className="text-center py-2">15min / 1H</td>
                      <td className="text-center py-2">4H / Daily</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">TP attendus</td>
                      <td className="text-center py-2">1-5%</td>
                      <td className="text-center py-2">5-20%</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">Vérification</td>
                      <td className="text-center py-2">Régulière</td>
                      <td className="text-center py-2">1-2x/jour</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          {/* TP Structure */}
          <Card className="bg-card/50 border-white/10">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" />
                Structure des Take Profits
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                Tous les trades utilisent une sortie partielle :
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-16 h-2 bg-green-500 rounded"></div>
                  <span><strong>TP1</strong> : 25% de la position</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-16 h-2 bg-blue-500 rounded"></div>
                  <span><strong>TP2</strong> : 25% de la position</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-16 h-2 bg-purple-500 rounded"></div>
                  <span><strong>TP100</strong> : 50% de la position</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Cette stratégie permet de sécuriser des gains tout en laissant courir une partie de la position.
              </p>
            </CardContent>
          </Card>
          
          {/* Intraday Warning Info */}
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-2 flex items-center gap-2 text-orange-400">
                <AlertTriangle className="w-4 h-4" />
                Avertissement Trades Intraday
              </h3>
              <p className="text-xs text-muted-foreground">
                Après 24h, vos trades Intraday afficheront un avertissement. Vous êtes libre de :
              </p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                <li>• Clôturer manuellement le trade</li>
                <li>• Le laisser ouvert si vous pensez qu'il atteindra son TP</li>
                <li>• Le convertir en trade Swing</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Intraday 24h Warning Badge Component
export const Intraday24hWarning = ({ trade, onClose, onConvertToSwing }) => {
  const createdAt = new Date(trade.created_at);
  const now = new Date();
  const diffMs = now - createdAt;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  const isOver24h = diffHours >= 24;
  
  if (!isOver24h || trade.trade_type !== 'INTRADAY') {
    return null;
  }
  
  return (
    <div className="mt-2 p-2 rounded-lg bg-orange-500/20 border border-orange-500/40 text-xs">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-orange-400">Trade Intraday &gt; 24h</p>
          <p className="text-muted-foreground">
            Ouvert depuis : {diffHours}h {diffMins}min
          </p>
          <div className="flex gap-2 mt-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="h-6 text-xs border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
              onClick={onClose}
            >
              Clôturer
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="h-6 text-xs border-primary/50 text-primary hover:bg-primary/20"
              onClick={onConvertToSwing}
            >
              Convertir en Swing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper hook for managing popup preferences
export const useInfoPopupPreferences = () => {
  const [showIntradayPopup, setShowIntradayPopup] = useState(false);
  const [showSwingPopup, setShowSwingPopup] = useState(false);
  
  const checkAndShowPopup = (tradeType) => {
    const key = `hideInfoPopup_${tradeType}`;
    const hidden = localStorage.getItem(key) === 'true';
    
    if (!hidden) {
      if (tradeType === 'intraday') {
        setShowIntradayPopup(true);
      } else if (tradeType === 'swing') {
        setShowSwingPopup(true);
      }
    }
  };
  
  const hidePopupPermanently = (tradeType) => {
    const key = `hideInfoPopup_${tradeType}`;
    localStorage.setItem(key, 'true');
  };
  
  return {
    showIntradayPopup,
    setShowIntradayPopup,
    showSwingPopup,
    setShowSwingPopup,
    checkAndShowPopup,
    hidePopupPermanently
  };
};

export default InfoGuidePanel;
